/**
 * Web Push subscription helper (#90, D13).
 *
 * Strategy (BYOD, cautious opt-in per D13):
 *   - Never call Notification.requestPermission() at login.
 *   - Caller (typically NotificationBell after a meaningful event) calls
 *     `tryEnablePush()` which prompts the browser permission and stores
 *     the subscription server-side.
 *   - If denied/dismissed, we record `push_prompt_dismissed_at` on the
 *     profile to enforce the 30-day cooldown.
 *
 * The VAPID public key must be set via REACT_APP_VAPID_PUBLIC_KEY.
 *
 * CORRECCION 2026-07-30: este bloque decia que cuando falta la clave "push is
 * silently disabled", y eso era exactamente el problema, no la documentacion de
 * una decision. Medido en prod: `push_subscriptions` tiene CERO filas en toda su
 * historia (contra 16.961 notificaciones y 11 perfiles) porque
 * REACT_APP_VAPID_PUBLIC_KEY no esta seteada en el proyecto de Vercel del
 * frontend. `tryEnablePush` cortaba con `{ ok:false, reason:'no_vapid_key' }`,
 * y el unico llamador (`NotificationContext.js:74`) descarta el resultado y
 * ademas hace `.catch(() => {})`. O sea: el sintoma de "falta una variable de
 * entorno" era, en produccion, literalmente nada.
 *
 * Ahora avisa por consola nombrando la variable — UNA sola vez por sesion,
 * porque `shouldPromptForPush` se llama en cada notificacion realtime y un warn
 * por evento es ruido que se aprende a ignorar (el mismo silencio con otro
 * disfraz). Degradar a in-app sigue siendo el comportamiento correcto (D13); lo
 * que cambia es que ahora deja rastro.
 */

import { supabase } from './supabase';

const PROMPT_COOLDOWN_DAYS = 30;

/**
 * `REACT_APP_*` es build-time en CRA: webpack la INLINEA en el bundle. Agregarla
 * en Vercel sin redesplegar el frontend no la enciende — el bundle servido sigue
 * siendo el que se compilo sin ella. Por eso el aviso lo dice explicitamente.
 */
const VAPID_SETUP_HINT =
  'Web Push DESACTIVADO: falta REACT_APP_VAPID_PUBLIC_KEY. Seteala en el proyecto de Vercel del ' +
  'frontend con la MISMA clave publica que VAPID_PUBLIC_KEY del mcp-server, y REDESPLEGA: en CRA ' +
  'las REACT_APP_* se inlinean en build time, agregarla sin rebuild no cambia el bundle. ' +
  'Mientras tanto las notificaciones son solo in-app (campana).';

let vapidWarningEmitted = false;

/**
 * Public key or null. Emits the setup warning at most once per page session.
 * Every caller must go through here — a raw `process.env` read is how the
 * silent branch came back the last two times.
 */
const getVapidPublicKey = () => {
  const key = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!key && !vapidWarningEmitted) {
    vapidWarningEmitted = true;
    // eslint-disable-next-line no-console
    console.warn(VAPID_SETUP_HINT);
  }
  return key || null;
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const getPushPermission = () => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  // Reuse if already registered for our SW
  const existing = await navigator.serviceWorker.getRegistration('/push-sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/push-sw.js');
};

export const tryEnablePush = async () => {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };

  const vapidKey = getVapidPublicKey();
  if (!vapidKey) return { ok: false, reason: 'no_vapid_key' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    await markPromptDismissed();
    return { ok: false, reason: permission };
  }

  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: 'sw_failed' };

  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
    } catch (e) {
      return { ok: false, reason: 'subscribe_failed', error: e };
    }
  }

  // Persist the subscription to push_subscriptions (RLS lets user insert own)
  const subJson = sub.toJSON();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { ok: false, reason: 'not_authenticated' };

  await supabase
    .from('push_subscriptions')
    .upsert(
      {
        user_id: user.id,
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        user_agent: navigator.userAgent,
        last_used_at: new Date().toISOString()
      },
      { onConflict: 'endpoint' }
    );

  return { ok: true };
};

export const markPromptDismissed = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  await supabase
    .from('profiles')
    .update({ push_prompt_dismissed_at: new Date().toISOString() })
    .eq('id', user.id);
};

export const shouldPromptForPush = async () => {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'default') return false;
  if (!getVapidPublicKey()) return false;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data: profile } = await supabase
    .from('profiles')
    .select('push_prompt_dismissed_at')
    .eq('id', user.id)
    .single();
  if (!profile?.push_prompt_dismissed_at) return true;
  const ageDays =
    (Date.now() - new Date(profile.push_prompt_dismissed_at).getTime()) / (1000 * 60 * 60 * 24);
  return ageDays >= PROMPT_COOLDOWN_DAYS;
};
